import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { painelApi, type LiveCharger } from '@/api/painel';
import { ErrorBox, SectionTitle, StatusLight } from '@/components/painel/Kpi';
import { PainelMapa } from '@/components/painel/PainelMapa';
import { useLoad, usePainelMe } from '@/components/painel/PainelContext';
import { StatusSummary } from '@/components/painel/StatusSummary';
import { fmtAgo, fmtBrl, fmtDateTime, fmtInt, fmtKwhNum } from '@/components/painel/format';

const POLL_MS = 30_000;

const ESTADO: Record<string, string> = {
  AVAILABLE: 'Livre',
  PREPARING: 'Cabo conectado',
  CHARGING: 'Carregando',
  UNAVAILABLE: 'Em uso',
  FINISHING: 'Finalizando',
  SUSPENDED_EV: 'Pausado pelo carro',
  SUSPENDED_EVSE: 'Pausado pelo carregador',
  OFFLINE: 'Sem comunicação',
  FAULTED: 'Com falha'
};

const estadoLabel = (estado: string | null) => (estado ? ESTADO[estado] ?? estado : 'sem dado');

function plugsLabel(ch: LiveCharger): string | null {
  if (ch.plugs == null) return null;
  const cabos = ch.conectores.length;
  if (ch.plugs === 1 && cabos > 1) return `${cabos} cabos, 1 por vez`;
  return `${fmtInt(ch.plugs)} ${ch.plugs === 1 ? 'plug' : 'plugs'}`;
}

/** Card do carregador: specs + estado por conector; "Ver mais" abre potência/SoC e o contato. */
function ChargerCard({ ch }: { ch: LiveCharger }) {
  const [aberto, setAberto] = useState(false);
  const specs = [
    ch.corrente,
    ch.potencia_nominal_kw > 0 ? `${fmtInt(ch.potencia_nominal_kw)} kW` : null,
    plugsLabel(ch),
    ch.preco_kwh_brl ? `${fmtBrl(ch.preco_kwh_brl)}/kWh no app Bow` : null
  ].filter(Boolean);

  return (
    <div className="rounded-3xl bg-white p-4 shadow-soft">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-[15px] font-semibold">{ch.nome}</div>
          <div className="text-[12px] text-bow-royal/55">{specs.join(' · ')}</div>
        </div>
        <StatusLight value={ch.semaforo} />
      </div>

      <div className="mt-3 flex items-end justify-between gap-3">
        <div>
          <div className="text-[11.5px] text-bow-royal/50">Energia acumulada</div>
          <div className="text-[20px] font-semibold leading-tight tabular-nums tracking-[-0.02em]">
            {fmtKwhNum(ch.kwh_acumulado)}
            <span className="ml-1 text-[12px] font-medium text-bow-royal/45">kWh</span>
          </div>
        </div>
        <button
          type="button"
          aria-expanded={aberto}
          onClick={() => setAberto((v) => !v)}
          className="h-8 shrink-0 rounded-full bg-bow-cream/70 px-3.5 text-[12.5px] font-semibold text-bow-royal/75 hover:bg-bow-cream"
        >
          {aberto ? 'Ver menos' : 'Ver mais'}
        </button>
      </div>

      {!aberto && (
        <ul className="m-0 mt-3 flex list-none flex-wrap gap-1.5 p-0">
          {ch.conectores.map((c) => (
            <li key={c.numero} className="rounded-full bg-bow-cream/60 px-2.5 py-1">
              <StatusLight value={c.semaforo} detail={`${c.numero} · ${estadoLabel(c.estado)}`} />
            </li>
          ))}
        </ul>
      )}

      {aberto && (
        <>
          <ul className="m-0 mt-3 flex list-none flex-col gap-2 p-0">
            {ch.conectores.map((c) => (
              <li
                key={c.numero}
                className="flex items-center justify-between gap-2 rounded-2xl bg-bow-cream/60 px-3 py-2.5 text-[13px]"
              >
                <span className="min-w-0">
                  <span className="font-semibold">Conector {c.numero}</span>
                  <span className="text-bow-royal/50">
                    {' '}
                    · {c.tipo} {Math.round(c.potencia_max_kw)} kW
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  {(c.potencia_kw != null || c.soc_pct != null) && (
                    <span className="tabular-nums font-semibold">
                      {c.potencia_kw != null && `${c.potencia_kw.toFixed(1).replace('.', ',')} kW`}
                      {c.potencia_kw != null && c.soc_pct != null && ' · '}
                      {c.soc_pct != null && `${Math.round(c.soc_pct)}%`}
                    </span>
                  )}
                  <StatusLight value={c.semaforo} detail={estadoLabel(c.estado)} />
                </span>
              </li>
            ))}
          </ul>
          <p className="m-0 mt-2.5 text-[11.5px] text-bow-royal/50">
            {ch.hub_station_id} · {ch.ultimo_contato ? `contato ${fmtAgo(ch.ultimo_contato)}` : 'sem contato registrado'}
            {' · '}energia acumulada desde o início do seu acesso (app Bow + roaming)
          </p>
        </>
      )}
    </div>
  );
}

/** Monitoramento ao vivo — só leitura, polling de 30 s (pausado com a aba oculta). */
export default function PainelEstacoes() {
  const { orgId = '' } = useParams();
  const me = usePainelMe();
  const org = me.orgs.find((o) => o.id === orgId);
  const live = useLoad(() => painelApi.stationsLive(orgId), [orgId]);
  const { reload } = live;

  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') reload();
    }, POLL_MS);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const d = live.data;
  return (
    <div className="pt-1">
      {live.error && !d && <ErrorBox message={live.error} onRetry={live.reload} />}
      {d && (
        <>
          <p className="m-0 text-[12.5px] text-bow-royal/55">
            Atualizado {fmtDateTime(d.atualizado_em)} · atualiza sozinho a cada 30 s
          </p>
          {!d.hub_disponivel && (
            <div className="mt-3 rounded-2xl bg-[#FFF4E0] px-4 py-3 text-[13px]">
              A central de comunicação com os carregadores está instável agora. O estado abaixo pode
              estar desatualizado — isso não indica falha no carregador.
            </div>
          )}
          {d.estacoes.length === 0 ? (
            <div className="mt-4 rounded-2xl bg-white p-5 text-[14px] text-bow-royal/70">
              Nenhum carregador com vínculo vigente hoje.
            </div>
          ) : (
            <div className="mt-3 rounded-3xl bg-white p-4 shadow-soft">
              <StatusSummary resumo={d.resumo} hubDisponivel={d.hub_disponivel} />
            </div>
          )}
          {org?.network_view && d.estacoes.length > 0 && (
            <>
              <SectionTitle>Mapa</SectionTitle>
              <PainelMapa estacoes={d.estacoes} />
            </>
          )}
          {d.estacoes.map((st) => (
            <section key={st.id} className="mt-5">
              <h2 className="m-0 text-[17px] font-semibold tracking-[-0.01em]">{st.nome}</h2>
              {st.endereco && <p className="m-0 mb-2 text-[12.5px] text-bow-royal/55">{st.endereco}</p>}
              <div className="mt-2 grid gap-2.5 lg:grid-cols-2">
                {st.carregadores.map((ch) => (
                  <ChargerCard key={ch.hub_station_id} ch={ch} />
                ))}
              </div>
            </section>
          ))}
        </>
      )}
    </div>
  );
}
